import React from 'react';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { formatDate, formatTime } from '../../../lib/dateFormat';

const WEEKDAY_NAMES = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

export type SubprofileContract = {
  enrollmentId: number;
  enrollmentStatus: string;
  subprofileId: string;
  startDate: string | null;
  startTime: string | null;
  weekday: number | null;
  durationMin: number | null;
  unterrichtsformName: string | null;
  fachName: string | null;
  teacherName: string | null;
  packageName: string | null;
  totalUnits: number | null;
  remainingUnits: number | null;
  usedUnits: number | null;
};

function statusBadge(status: string) {
  const normalized = status.toLowerCase();
  if (normalized === 'converted') {
    return { label: 'Aktiv', className: 'border-emerald-300 bg-emerald-50 text-emerald-700' };
  }
  if (normalized === 'accepted_by_student') {
    return { label: 'In Bearbeitung', className: 'border-amber-300 bg-amber-50 text-amber-800' };
  }
  if (normalized === 'rejected_by_student') {
    // Der Kunde hat das Anmeldeangebot abgelehnt (T-0108).
    return { label: 'Vom Kunden abgelehnt', className: 'border-red-300 bg-red-50 text-red-700' };
  }
  if (normalized === 'ended' || normalized === 'cancelled') {
    return { label: 'Beendet', className: 'border-slate-300 bg-slate-50 text-slate-600' };
  }
  return { label: status, className: 'border-slate-300 bg-slate-50 text-slate-600' };
}

export function SubprofileContractCard({ contract }: { contract: SubprofileContract }) {
  const badge = statusBadge(contract.enrollmentStatus);
  const title =
    contract.fachName ||
    contract.unterrichtsformName ||
    `Vertrag #${contract.enrollmentId}`;
  const weekdayLabel =
    contract.weekday !== null && contract.weekday !== undefined
      ? WEEKDAY_NAMES[contract.weekday % 7] ?? ''
      : '';
  const timeLabel = contract.startTime ? formatTime(contract.startTime) : '';
  const scheduleLabel = [weekdayLabel, timeLabel].filter(Boolean).join(' ');

  return (
    <Card className="overflow-hidden border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base">{title}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {contract.teacherName ? `Lehrer: ${contract.teacherName}` : 'Lehrer: –'}
              {scheduleLabel ? ` · ${scheduleLabel}` : ''}
              {contract.startDate ? ` · ab ${formatDate(contract.startDate)}` : ''}
            </p>
          </div>
          <Badge variant="outline" className={badge.className}>
            {badge.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="grid gap-2 sm:grid-cols-3 text-sm">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Paket</p>
          <p className="font-medium">{contract.packageName || '–'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Einheiten</p>
          <p className="font-medium">
            {contract.totalUnits != null
              ? `${contract.remainingUnits ?? 0} / ${contract.totalUnits} übrig`
              : '–'}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Dauer</p>
          <p className="font-medium">
            {contract.durationMin ? `${contract.durationMin} Min.` : '–'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
